import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface GeneratedReview {
  id: string;
  business_id: string;
  category_id: string | null;
  review_text: string;
  category_name: string;
  keywords: string[];
  usp: string | null;
  tone: string;
  style: string;
  word_limit: string | null;
  language: string;
  copied: boolean;
  submitted: boolean;
  created_at: string;
}

export function useReviews(businessId: string | undefined) {
  const [reviews, setReviews] = useState<GeneratedReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (businessId) {
      fetchReviews();
    } else {
      setLoading(false);
    }
  }, [businessId]);

  const fetchReviews = async () => {
    if (!businessId) return;

    try {
      const { data, error } = await supabase
        .from('generated_reviews')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveReviews = async (newReviews: Omit<GeneratedReview, 'id' | 'created_at' | 'copied' | 'submitted'>[]) => {
    if (!businessId) return { error: new Error('No business found') };

    try {
      const reviewsToInsert = newReviews.map(review => ({
        ...review,
        business_id: businessId,
      }));

      const { data, error } = await supabase
        .from('generated_reviews')
        .insert(reviewsToInsert)
        .select();

      if (error) throw error;
      
      setReviews([...data, ...reviews]);
      return { data };
    } catch (err) {
      const error = err as Error;
      toast.error(error.message);
      return { error };
    }
  };

  const markAsCopied = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from('generated_reviews')
        .update({ copied: true })
        .eq('id', reviewId);

      if (error) throw error;
      
      setReviews(reviews.map(r => r.id === reviewId ? { ...r, copied: true } : r));
    } catch (err) {
      console.error('Error marking as copied:', err);
    }
  };

  const markAsSubmitted = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from('generated_reviews')
        .update({ submitted: true })
        .eq('id', reviewId);

      if (error) throw error;
      
      setReviews(reviews.map(r => r.id === reviewId ? { ...r, submitted: true } : r));
      toast.success('Review marked as submitted!');
    } catch (err) {
      console.error('Error marking as submitted:', err);
    }
  };

  const stats = {
    reviewsGenerated: reviews.length,
    reviewsCopied: reviews.filter(r => r.copied).length,
    reviewsSubmitted: reviews.filter(r => r.submitted).length,
  };

  return {
    reviews,
    loading,
    saveReviews,
    markAsCopied,
    markAsSubmitted,
    stats,
    refetch: fetchReviews,
  };
}
